import Phaser from 'phaser';

/**
 * Paper.io 스타일 3D 정육면체 렌더러
 * 앞면이 항상 마우스 포인터 방향을 향함
 */
export class CubeRenderer {
  private graphics: Phaser.GameObjects.Graphics;
  private baseColor: number;
  private direction: number = 0;

  // 면 색상 (고정: 앞면/왼쪽/오른쪽)
  private topColor: number = 0xFFFFFF;
  private frontColor: number = 0xCCCCCC;  // 앞면 (마우스 방향) - 밝음
  private leftColor: number = 0x888888;   // 왼쪽면 - 어두움
  private rightColor: number = 0xAAAAAA;  // 오른쪽면 - 중간

  // 정육면체 크기
  private baseCubeSize: number; // 배율 적용 전 기준 크기
  private cubeSize: number;
  private sizeMultiplier: number = 1;
  private rainbow: boolean = false;

  // 아이소메트릭 각도 (30도)
  private isoAngle: number = Math.PI / 6;

  constructor(scene: Phaser.Scene, size: number, color: number) {
    this.baseCubeSize = size * 0.4;
    this.cubeSize = this.baseCubeSize;
    this.baseColor = color;
    this.graphics = scene.add.graphics();

    this.calculateColors(color);
  }

  private toPastelColor(color: number): number {
    const r = (color >> 16) & 0xFF;
    const g = (color >> 8) & 0xFF;
    const b = color & 0xFF;

    const pastelR = Math.floor(r + (255 - r) * 0.35);
    const pastelG = Math.floor(g + (255 - g) * 0.35);
    const pastelB = Math.floor(b + (255 - b) * 0.35);

    return (pastelR << 16) | (pastelG << 8) | pastelB;
  }

  private lightenColor(color: number, amount: number): number {
    const r = Math.min(255, ((color >> 16) & 0xFF) + 255 * amount);
    const g = Math.min(255, ((color >> 8) & 0xFF) + 255 * amount);
    const b = Math.min(255, (color & 0xFF) + 255 * amount);
    return (Math.floor(r) << 16) | (Math.floor(g) << 8) | Math.floor(b);
  }

  private darkenColor(color: number, amount: number): number {
    const r = Math.max(0, ((color >> 16) & 0xFF) * (1 - amount));
    const g = Math.max(0, ((color >> 8) & 0xFF) * (1 - amount));
    const b = Math.max(0, (color & 0xFF) * (1 - amount));
    return (Math.floor(r) << 16) | (Math.floor(g) << 8) | Math.floor(b);
  }

  private calculateColors(color: number): void {
    const pastel = this.toPastelColor(color);
    this.topColor = this.lightenColor(pastel, 0.2);
    this.frontColor = this.lightenColor(pastel, 0.05); // 앞면: 밝음
    this.rightColor = pastel;                           // 오른쪽: 기본
    this.leftColor = this.darkenColor(pastel, 0.25);    // 왼쪽: 어두움
  }

  setDirection(angle: number): void {
    this.direction = angle;
  }

  setColor(color: number): void {
    this.baseColor = color;
    this.calculateColors(color);
  }

  /**
   * 3D 좌표를 2D 아이소메트릭 좌표로 변환
   */
  private toIso(x3d: number, y3d: number, z3d: number): { x: number; y: number } {
    const isoX = (x3d - z3d) * Math.cos(this.isoAngle);
    const isoY = (x3d + z3d) * Math.sin(this.isoAngle) - y3d;
    return { x: isoX, y: isoY };
  }

  render(x: number, y: number): void {
    this.graphics.clear();

    // 무적 무지개 깜빡임: 원래 색 ↔ 무지개를 번갈아 (0.8초 주기)
    if (this.rainbow) {
      const phase = (Date.now() % 800) / 800;
      if (phase < 0.5) {
        this.calculateColors(this.baseColor);
      } else {
        const h = (Date.now() % 1200) / 1200;
        const c = Phaser.Display.Color.HSVToRGB(h, 0.9, 1) as Phaser.Display.Color;
        this.calculateColors(c.color);
      }
    }

    const s = this.cubeSize;

    // 앞면(+Z)이 마우스 방향을 향하도록 회전
    const rot = this.direction + Math.PI / 4;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);

    // 정육면체 8개 꼭지점 (앞면이 +Z 방향)
    const vertices = [
      { x: -s, y: 0, z: -s },     // 0: 아래 뒤 왼쪽
      { x: s, y: 0, z: -s },      // 1: 아래 뒤 오른쪽
      { x: s, y: 0, z: s },       // 2: 아래 앞 오른쪽
      { x: -s, y: 0, z: s },      // 3: 아래 앞 왼쪽
      { x: -s, y: s * 2, z: -s }, // 4: 위 뒤 왼쪽
      { x: s, y: s * 2, z: -s },  // 5: 위 뒤 오른쪽
      { x: s, y: s * 2, z: s },   // 6: 위 앞 오른쪽
      { x: -s, y: s * 2, z: s },  // 7: 위 앞 왼쪽
    ];

    // Y축 기준 회전 적용 후 아이소메트릭 투영
    const projected = vertices.map(v => {
      const rx = v.x * cos - v.z * sin;
      const rz = v.x * sin + v.z * cos;
      const iso = this.toIso(rx, v.y, rz);
      return { x: x + iso.x, y: y + iso.y };
    });

    // 그림자 (큐브 바닥면을 6시 방향으로 오프셋)
    const shadowOffsetY = 8;
    this.graphics.fillStyle(0x000000, 0.2);
    this.graphics.beginPath();
    this.graphics.moveTo(projected[0].x, projected[0].y + shadowOffsetY);
    this.graphics.lineTo(projected[1].x, projected[1].y + shadowOffsetY);
    this.graphics.lineTo(projected[2].x, projected[2].y + shadowOffsetY);
    this.graphics.lineTo(projected[3].x, projected[3].y + shadowOffsetY);
    this.graphics.closePath();
    this.graphics.fillPath();

    // 4개 측면 정의
    const sideFaces = [
      { name: 'back', verts: [0, 1, 5, 4], color: this.leftColor },
      { name: 'right', verts: [1, 2, 6, 5], color: this.rightColor },
      { name: 'front', verts: [2, 3, 7, 6], color: this.frontColor },
      { name: 'left', verts: [3, 0, 4, 7], color: this.leftColor },
    ];

    // 각 면의 중심 Y좌표 계산 (투영 후) - Y가 클수록 화면 아래 = 앞쪽
    const facesWithDepth = sideFaces.map(face => {
      // 면의 아래쪽 두 점(바닥)의 평균 Y로 깊이 판단
      const bottomY = (projected[face.verts[0]].y + projected[face.verts[1]].y) / 2;
      return { face, depth: bottomY };
    });

    // 깊이 순 정렬 (Y가 작은 것 = 화면 위쪽 = 뒤쪽 먼저 그림)
    facesWithDepth.sort((a, b) => a.depth - b.depth);

    // 모든 측면 그리기 (뒤에서 앞으로)
    for (const { face } of facesWithDepth) {
      this.graphics.fillStyle(face.color, 1);
      this.graphics.beginPath();
      this.graphics.moveTo(projected[face.verts[0]].x, projected[face.verts[0]].y);
      for (let i = 1; i < 4; i++) {
        this.graphics.lineTo(projected[face.verts[i]].x, projected[face.verts[i]].y);
      }
      this.graphics.closePath();
      this.graphics.fillPath();
    }

    // 윗면 (항상 보임)
    this.graphics.fillStyle(this.topColor, 1);
    this.graphics.beginPath();
    this.graphics.moveTo(projected[4].x, projected[4].y);
    this.graphics.lineTo(projected[5].x, projected[5].y);
    this.graphics.lineTo(projected[6].x, projected[6].y);
    this.graphics.lineTo(projected[7].x, projected[7].y);
    this.graphics.closePath();
    this.graphics.fillPath();
  }

  setDepth(depth: number): void {
    this.graphics.setDepth(depth);
  }

  setPosition(x: number, y: number): void {
    this.graphics.setPosition(x, y);
  }

  destroy(): void {
    this.graphics.destroy();
  }

  getGraphics(): Phaser.GameObjects.Graphics {
    return this.graphics;
  }

  getColor(): number {
    return this.baseColor;
  }

  getBaseWidth(): number {
    return this.cubeSize * 2;
  }

  getTotalHeight(): number {
    return this.cubeSize * 2 + this.cubeSize * Math.sin(this.isoAngle);
  }

  /** 거대화/축소: 큐브 크기를 기준 크기의 배율로 설정 */
  setSizeMultiplier(m: number): void {
    if (this.sizeMultiplier === m) return;
    this.sizeMultiplier = m;
    this.cubeSize = this.baseCubeSize * m;
  }

  /** 무적 무지개 깜빡임 모드 */
  setRainbow(on: boolean): void {
    if (this.rainbow === on) return;
    this.rainbow = on;
    if (!on) this.calculateColors(this.baseColor); // 원색 복원
  }
}
